import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import Swal from '../../utils/swal.util';
import {
  BannerCropComponent,
  BannerCropResult
} from '../banner-crop/banner-crop.component';
import { LucideModule } from '../../modules/lucide.module';
import { TemaPortalService } from '../../utils/tema-portal.service';
import { TemaPortal } from '../../utils/tema-portal.model';
import {
  IMAGEM_PAGINA,
  ImagemPagina,
  TIPOS_IMAGEM_PAGINA,
  imagemPaginaVazia,
  mesmaImagem
} from './pagina-previa.model';

export type FormatoPrevia = 'desktop' | 'mobile';

/** Cada accordion que a página publica: só o cabeçalho aparece na prévia. */
export interface SecaoPrevia {
  id: number;
  titulo: string;
  icone: string;
}

/**
 * Prévia das páginas de perguntas frequentes, política de atendimento e canais.
 *
 * Espelha o `pages-perguntas-politica-canais` do sai-carta-servico: título com os
 * enfeites, subtítulo, ilustração à esquerda e, à direita, a busca com a lista de
 * accordions. Serve para conferir, antes de publicar, como a página fica com a
 * imagem exibida, oculta ou trocada — quando a imagem sai, o portal passa a uma
 * coluna só, e a prévia faz o mesmo.
 *
 * A prévia abre fechada e edita uma cópia: nada vai para a página antes do Salvar.
 */
@Component({
  selector: 'app-pagina-previa',
  standalone: true,
  imports: [CommonModule, LucideModule],
  templateUrl: './pagina-previa.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pagina-previa.component.css'
})
export class PaginaPreviaComponent {
  private readonly temaPortalService = inject(TemaPortalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** Título da página no portal, com os enfeites ao lado. */
  @Input() titulo = '';

  @Input() subtitulo = '';

  @Input() rotuloBusca = 'Buscar';

  /** Imagem publicada hoje; a prévia trabalha sobre uma cópia dela. */
  @Input()
  set imagem(valor: ImagemPagina) {
    this.publicada = { ...valor };
    this.emEdicao = { ...valor };
  }

  /** Cabeçalhos dos accordions publicados, na ordem em que o cidadão vê. */
  @Input() secoes: SecaoPrevia[] = [];

  /** Imagem confirmada no Salvar, para a tela gravar na página. */
  @Output() salva = new EventEmitter<ImagemPagina>();

  readonly temaPortal: TemaPortal = this.temaPortalService.atual();
  readonly limites = IMAGEM_PAGINA;

  aberta = false;
  arrastando = false;
  formatoPrevia: FormatoPrevia = 'desktop';

  emEdicao: ImagemPagina = imagemPaginaVazia();

  private publicada: ImagemPagina = imagemPaginaVazia();

  /** O que a página tem cadastrado, mesmo que esteja oculto no portal. */
  get temImagem(): boolean {
    return !!this.emEdicao.imagem;
  }

  /** Tela estreita esconde a ilustração no portal, igual ao `@media` da página. */
  get comImagem(): boolean {
    return this.emEdicao.exibirImagem && this.temImagem && this.formatoPrevia === 'desktop';
  }

  get alterada(): boolean {
    return !mesmaImagem(this.emEdicao, this.publicada);
  }

  get podeSalvar(): boolean {
    return this.alterada && (!this.emEdicao.exibirImagem || this.temImagem);
  }

  alternarPrevia(): void {
    this.aberta = !this.aberta;
  }

  definirFormatoPrevia(formato: FormatoPrevia): void {
    this.formatoPrevia = formato;
  }

  /** Ocultar não descarta o arquivo: ele volta ao religar a imagem. */
  alternarExibirImagem(): void {
    this.emEdicao = { ...this.emEdicao, exibirImagem: !this.emEdicao.exibirImagem };
    this.changeDetectorRef.markForCheck();
  }

  aoSelecionarArquivo(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const arquivo = input.files?.[0] ?? null;

    input.value = '';
    this.processarArquivo(arquivo);
  }

  aoArrastar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = true;
  }

  aoSairDoArraste(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = false;
  }

  aoSoltar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = false;
    this.processarArquivo(evento.dataTransfer?.files?.[0] ?? null);
  }

  salvar(): void {
    if (!this.podeSalvar) {
      return;
    }

    this.publicada = { ...this.emEdicao };
    this.salva.emit({ ...this.emEdicao });
  }

  trackBySecao(_index: number, secao: SecaoPrevia): number {
    return secao.id;
  }

  /** Recusa formato e tamanho fora do padrão antes de abrir o recorte. */
  private processarArquivo(arquivo: File | null): void {
    if (!arquivo) {
      return;
    }

    if (!TIPOS_IMAGEM_PAGINA.includes(arquivo.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Formato não aceito',
        text: 'Envie a imagem em PNG ou JPG.'
      });

      return;
    }

    if (arquivo.size > IMAGEM_PAGINA.tamanhoMaximoMb * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Arquivo muito grande',
        text: `A imagem deve ter no máximo ${IMAGEM_PAGINA.tamanhoMaximoMb}MB.`
      });

      return;
    }

    this.abrirRecorte(arquivo);
  }

  private abrirRecorte(arquivo: File): void {
    const cropRef = this.dialog.open(BannerCropComponent, {
      data: {
        arquivo,
        largura: IMAGEM_PAGINA.largura,
        altura: IMAGEM_PAGINA.altura,
        descricao: `Ajuste o enquadramento da imagem de ${this.titulo.toLowerCase()}.`
      },
      width: '760px',
      maxWidth: '95vw'
    });

    cropRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultado: BannerCropResult | undefined) => {
        if (!resultado) {
          return;
        }

        this.emEdicao = {
          exibirImagem: true,
          imagem: resultado.base64,
          nomeImagem: resultado.arquivo.name,
          arquivo: resultado.arquivo
        };

        this.changeDetectorRef.detectChanges();
      });
  }
}
